import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from './Navbar';
import Footer from './Footer';

const programDetails = {
  'Student Development Programme': {
    tagline: 'Bridge the Gap Between Campus & Industry',
    overview: 'Our Student Development Programme (SDP) is an intensive, hands-on initiative designed to transform final-year and pre-final-year students into industry-ready professionals. Through live projects, expert mentorship, and certification, we ensure every participant walks out with real-world experience.',
    highlights: [
      { title: 'Live Project Exposure', desc: 'Work on actual client projects under professional guidance — not simulated case studies.' },
      { title: 'Industry Mentorship', desc: 'One-on-one and group sessions with experienced engineers and product managers.' },
      { title: 'Certifications Awarded', desc: 'Recognised completion certificates that strengthen your resume and LinkedIn profile.' },
      { title: 'Placement Assistance', desc: 'Resume reviews, mock interviews, and direct referrals to our client network.' },
      { title: 'Tech Stack Coverage', desc: 'Covers web, mobile, AI/ML, cloud, and UI/UX based on your chosen track.' },
      { title: 'Soft Skills & Communication', desc: 'Presentation skills, team collaboration, and professional communication workshops.' },
    ],
    eligibility: 'B.Tech / B.E / MCA / M.Tech students (2nd year onwards)',
    duration: '4 – 12 Weeks (Flexible tracks)',
    mode: 'Hybrid (Online + On-site)',
    tracks: ['Web Development', 'Mobile Apps', 'Artificial Intelligence', 'Cloud & DevOps', 'UI/UX Design', 'Data Engineering'],
  },
  'Faculty Development Programme': {
    tagline: 'Equipping Educators for the Digital Age',
    overview: 'The Faculty Development Programme (FDP) empowers college educators with the latest industry knowledge, emerging technologies, and modern pedagogical techniques. Our expert-led workshops help faculty design and deliver curriculum that truly prepares students for the tech industry.',
    highlights: [
      { title: 'Technology Workshops', desc: 'Deep-dive workshops on AI, cloud computing, full-stack development, and more.' },
      { title: 'Curriculum Design Support', desc: 'Help restructure existing syllabi to align with current industry needs and standards.' },
      { title: 'Industry Collaboration', desc: 'Connect faculty with industry professionals for guest lectures and joint research.' },
      { title: 'Research Guidance', desc: 'Support for faculty pursuing publications, patents, and applied research projects.' },
      { title: 'Certification & Recognition', desc: 'Completion certificates and recognition from QuBright Technologies LLP.' },
      { title: 'Hands-On Labs', desc: 'Practical lab sessions with real tools — not just theoretical overviews.' },
    ],
    eligibility: 'Faculty members from Engineering, Science & Technology departments',
    duration: '2 – 5 Days (Intensive) or 4-Week online',
    mode: 'On-campus (College premises) or Online',
    tracks: ['AI & Machine Learning', 'Cloud Computing', 'Full-Stack Web Dev', 'Cybersecurity', 'IoT & Embedded Systems', 'Data Science'],
  },
  'Internship Programme': {
    tagline: 'Real Projects. Real Teams. Real Impact.',
    overview: 'Our structured Internship Programme places students inside QuBright as junior team members working on live client deliverables. Across 6 technology tracks, interns collaborate with senior engineers, attend daily standups, and earn a recognised certificate — plus a stipend for top performers.',
    highlights: [
      { title: '6 Technology Tracks', desc: 'Choose from Web, Mobile, AI/ML, Cloud, UI/UX, or Data Engineering.' },
      { title: 'Real Client Projects', desc: 'Contribute to actual products and solutions we deliver to paying clients.' },
      { title: 'Weekly Evaluations', desc: 'Structured performance reviews every week with actionable feedback.' },
      { title: 'Stipend for Top Performers', desc: 'Top 20% of performers receive a performance-based stipend.' },
      { title: 'Certificate of Completion', desc: 'Industry-recognised completion certificate, verified on our website.' },
      { title: 'LinkedIn Recommendation', desc: 'Outstanding interns receive a personal LinkedIn recommendation from our CTO.' },
    ],
    eligibility: 'Students from any technical background (graduation or above)',
    duration: '1 – 6 Months',
    mode: 'Remote / Hybrid',
    tracks: ['Web Development', 'Mobile Apps', 'AI & Machine Learning', 'Cloud & DevOps', 'UI/UX Design', 'Data Engineering'],
  },
  'College Collaboration': {
    tagline: 'Bringing Industry Inside Your Campus',
    overview: 'Through formal MoU partnerships with colleges and universities, QuBright co-delivers industry-grade training, organises hackathons, and participates in campus placement drives. We become an extended arm of your placement and training cell — at no cost to the institution.',
    highlights: [
      { title: 'MoU with Institutions', desc: 'Formal signed agreements that enable structured collaboration over multiple years.' },
      { title: 'Campus Hackathons', desc: 'We organise and sponsor hackathons, ideathons, and coding competitions on your campus.' },
      { title: 'Tech Symposiums', desc: 'Annual tech events with industry speakers, panel discussions, and demos.' },
      { title: 'Placement Drives', desc: 'Direct campus recruitment for top-performing students across our partner companies.' },
      { title: 'Guest Lectures', desc: 'Regular guest sessions by QuBright engineers, designers, and founders.' },
      { title: 'Free SDP & FDP', desc: 'Partner institutions get priority access to our SDP and FDP programmes at subsidised rates.' },
    ],
    eligibility: 'Engineering and Technology Colleges / Universities',
    duration: 'Ongoing (MoU valid 1–3 years, renewable)',
    mode: 'On-campus',
    tracks: ['SDP Integration', 'FDP Delivery', 'Hackathons', 'Placement Drives', 'Tech Fests', 'Research Collaboration'],
  },
  'Research Support': {
    tagline: 'Accelerating Academic and Applied Research',
    overview: 'QuBright offers robust technical and conceptual support for students and scholars engaged in advanced research. From assisting with complex implementations in AI and Data Science to guiding the publication process, we help bridge the gap between academic theory and practical innovation.',
    highlights: [
      { title: 'Implementation Assistance', desc: 'Technical help with coding complex algorithms, simulations, and data models.' },
      { title: 'Paper Publication Guidance', desc: 'Support in drafting, formatting, and submitting research papers to reputable journals.' },
      { title: 'Data Analysis & Modeling', desc: 'Expert assistance in statistical analysis and machine learning model training.' },
      { title: 'Literature Review', desc: 'Help in identifying and synthesizing relevant academic and industry literature.' },
      { title: 'Patent Filing Support', desc: 'Guidance through the intellectual property protection and patent application process.' },
      { title: 'Hardware Prototyping', desc: 'For IoT and embedded systems, we provide lab access and prototyping assistance.' },
    ],
    eligibility: 'M.Tech / PhD Scholars and Research-oriented UG students',
    duration: 'Project-based (3 - 12 Months)',
    mode: 'Hybrid (Online + On-site)',
    tracks: ['AI & Deep Learning', 'IoT & Embedded', 'Data Science', 'Cybersecurity', 'Cloud Computing', 'Blockchain'],
  },
  'Software Solutions': {
    tagline: 'Custom Software Engineered for Corporate Growth',
    overview: 'Beyond training, QuBright is a full-fledged technology partner. We build bespoke software solutions tailored to solve complex corporate challenges, streamline operations, and drive digital transformation across various industries.',
    highlights: [
      { title: 'Custom Web & Mobile Apps', desc: 'End-to-end development of scalable applications tailored to your business processes.' },
      { title: 'Enterprise AI Integration', desc: 'Implementing custom LLMs, predictive analytics, and automated workflows.' },
      { title: 'Cloud Infrastructure', desc: 'Architecting secure, highly available, and cost-optimized cloud environments.' },
      { title: 'Legacy System Modernization', desc: 'Upgrading outdated systems to modern tech stacks without business disruption.' },
      { title: 'Data Engineering Pipelines', desc: 'Building robust data lakes and BI dashboards for data-driven decision making.' },
      { title: 'Dedicated Tech Teams', desc: 'Providing dedicated teams of engineers and designers to augment your internal capabilities.' },
    ],
    eligibility: 'Startups, SMEs, and Enterprise Corporations',
    duration: 'Ongoing / Project-based',
    mode: 'Remote / On-site consulting',
    tracks: ['Enterprise Applications', 'SaaS Development', 'AI/ML Solutions', 'Cloud Migration', 'Digital Transformation', 'Data Analytics'],
  },
  'Placement Assistance': {
    tagline: 'Connecting Top Talent with Leading Enterprises',
    overview: 'QuBright bridges the talent gap by connecting our highly trained students and interns with corporate partners seeking skilled professionals. We offer a streamlined hiring pipeline for companies looking for pre-vetted, industry-ready talent.',
    highlights: [
      { title: 'Pre-vetted Candidates', desc: 'Access to students who have successfully completed our rigorous SDP or Internship programs.' },
      { title: 'Custom Hiring Drives', desc: 'We organize exclusive recruitment drives tailored to your specific technical requirements.' },
      { title: 'Zero Recruitment Fees', desc: 'We offer placement assistance as a value-add service without hefty placement agency fees.' },
      { title: 'Skill-matched Profiles', desc: 'Candidates matched precisely based on their track (e.g., React, Python, AWS).' },
      { title: 'Soft Skills Training', desc: 'Our candidates undergo communication and professional etiquette training.' },
      { title: 'Day-One Ready', desc: 'Since candidates train on live projects, they require minimal onboarding time.' },
    ],
    eligibility: 'Companies actively hiring junior and mid-level technical talent',
    duration: 'On-demand',
    mode: 'Remote / On-site interviews',
    tracks: ['Frontend Developers', 'Backend Developers', 'AI/ML Engineers', 'Cloud DevOps', 'UI/UX Designers', 'Data Analysts'],
  },
};

export default function ProgramDetail({ program, onClose }) {
  const details = programDetails[program.title] || {};

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 40 }}
      transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="fixed inset-0 z-[200] bg-white overflow-y-auto"
    >
      <div className="relative z-50"><Navbar forceOpaque={true} /></div>

      {/* Hero Banner */}
      <div className={"relative pt-20 pb-0 w-full overflow-hidden bg-gradient-to-br " + program.color}>
        <div className="absolute inset-0 grid-pattern opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white" />

        <motion.button
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          onClick={onClose}
          className="absolute top-24 left-6 md:left-12 flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white font-space text-sm font-semibold hover:bg-white/25 transition-colors"
        >
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
          </svg>
          Back
        </motion.button>

        <div className="max-w-6xl mx-auto px-6 md:px-16 pt-16 pb-20">
          <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2, duration: 0.75 }}>
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-space font-bold uppercase tracking-widest mb-4 text-white bg-white/15 backdrop-blur-sm border border-white/20">
              {program.short} — QuBright Training
            </span>
            <h1 className="font-syne font-extrabold text-4xl md:text-6xl text-white leading-tight mb-4">{program.title}</h1>
            {details.tagline && <p className="font-space text-lg text-white/75 max-w-2xl">{details.tagline}</p>}

            {/* Quick meta pills */}
            <div className="flex flex-wrap gap-3 mt-8">
              {[
                { label: details.duration, icon: '⏱' },
                { label: details.mode, icon: '📍' },
                { label: details.eligibility, icon: '🎓' },
              ].map((m, i) => m.label && (
                <span key={i} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 text-white text-sm font-inter">
                  <span>{m.icon}</span>{m.label}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Body */}
      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-16 py-20">

          {/* Overview */}
          <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.35 }} className="mb-16">
            <h2 className="font-syne font-extrabold text-3xl text-slate-900 mb-4">Programme <span className="gradient-text-blue">Overview</span></h2>
            <p className="font-inter text-slate-600 text-lg leading-relaxed max-w-4xl">{details.overview}</p>
          </motion.div>

          <div className="grid lg:grid-cols-5 gap-14">
            {/* Left — Highlights */}
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.45 }} className="lg:col-span-3">
              <h2 className="font-syne font-extrabold text-2xl text-slate-900 mb-6">
                What You <span className="gradient-text-blue">Get</span>
              </h2>
              <div className="space-y-4">
                {(details.highlights || []).map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ x: -24, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.5 + i * 0.07 }}
                    className={"flex gap-5 p-5 rounded-2xl bg-slate-50 border border-slate-100 hover:bg-blue-50/30 hover:border-brand-blue/20 transition-all duration-300 group"}
                  >
                    <div className={"w-9 h-9 rounded-xl bg-gradient-to-br " + program.color + " flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-300 mt-0.5"}>
                      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-syne font-bold text-slate-900 text-base mb-1">{item.title}</h4>
                      <p className="font-inter text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right — Tracks + CTA */}
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.55 }} className="lg:col-span-2 space-y-6">

              {/* Tracks card */}
              <div className="rounded-3xl border border-slate-200 bg-white shadow-card overflow-hidden">
                <div className={"bg-gradient-to-r " + program.color + " px-6 py-5"}>
                  <h3 className="font-syne font-bold text-xl text-white">Available Tracks</h3>
                  <p className="font-inter text-white/75 text-sm mt-1">Choose your technology focus area</p>
                </div>
                <div className="p-6">
                  <div className="flex flex-wrap gap-2">
                    {(details.tracks || []).map((track, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-slate-700 text-xs font-space font-medium hover:border-brand-blue/30 hover:bg-blue-50 transition-colors">
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-blue flex-shrink-0" />
                        {track}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* CTA card */}
              <div className={"rounded-3xl bg-gradient-to-br " + program.color + " p-8 text-white shadow-royal"}>
                <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center mb-5">
                  <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                  </svg>
                </div>
                <h3 className="font-syne font-bold text-2xl mb-3">Ready to Enrol?</h3>
                <p className="font-inter text-white/75 text-sm leading-relaxed mb-6">
                  Contact us today to reserve your spot or to discuss a college partnership.
                </p>
                <a href="#contact" onClick={onClose} className="block text-center bg-white font-space font-bold text-sm py-3.5 rounded-2xl hover:bg-white/90 transition-colors shadow-lg" style={{ color: program.accent }}>
                  Enrol / Get in Touch
                </a>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Why QuBright strip */}
        <div className="bg-slate-50 border-t border-slate-100">
          <div className="max-w-6xl mx-auto px-6 md:px-16 py-14">
            <div className="text-center mb-10">
              <h2 className="font-syne font-extrabold text-3xl text-slate-900">Why <span className="gradient-text">QuBright?</span></h2>
              <p className="font-inter text-slate-500 mt-2">Your trusted partner for industry-grade education</p>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-5">
              {[
                { icon: '🏫', title: '10+ College Partners', desc: 'Trusted by institutions across the region for quality training.' },
                { icon: '👩‍💻', title: '200+ Students Trained', desc: 'Real outcomes — not just enrollments.' },
                { icon: '🏆', title: '95% Placement Rate', desc: 'Our students get placed. That is our measure of success.' },
                { icon: '⚡', title: 'Industry-Led Content', desc: 'Curriculum built by practitioners, not just academics.' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 1.0 + i * 0.1 }}
                  className="bg-white rounded-2xl p-6 border border-slate-200 shadow-card text-center hover:border-brand-blue/25 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="text-3xl mb-3">{item.icon}</div>
                  <h4 className="font-syne font-bold text-slate-900 text-sm mb-2">{item.title}</h4>
                  <p className="font-inter text-slate-500 text-xs leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </motion.div>
  );
}
